import { Checkbox } from '@/global/components/Checkbox'
import { Badge } from '@/global/components/ui/badge'
import { Button } from '@/global/components/ui/button'
import { Popover, PopoverContent } from '@/global/components/ui/popover'
import { cn } from '@/lib/utils'
import {
    Building2Icon,
    FolderTreeIcon,
    MapPinIcon,
    RotateCcwIcon,
    UsersIcon,
} from 'lucide-react'
import React from 'react'
import { Label } from '../ui/label'
import { ShellContext, useEscopoContext } from './context'
import { arr, FACETAS, LABEL, type FacetaId } from './escopo'

/** Altura fixa do painel: evita o popover crescer/encolher ao trocar de faceta. */
const ALTURA_PAINEL = 'h-[300px]'

/** Ícone por faceta — compartilhado com os badges do `Trigger`. */
export const ICONES_FACETA: Record<
    FacetaId,
    React.ComponentType<{ className?: string }>
> = {
    empresas: Building2Icon,
    centrosCusto: FolderTreeIcon,
    locais: MapPinIcon,
    funcionarios: UsersIcon,
}

/**
 * Shell popover: rail de facetas com contadores + painel da faceta ativa +
 * rodapé com a contagem do escopo. As partes ficam montadas fora do popover
 * para que o registro (ordem e itens do rail) não se perca ao fechar.
 */
export function Cascata({ children }: { children: React.ReactNode }) {
    const {
        registro,
        ativa,
        setAtiva,
        valor,
        dados,
        limpar,
        aberto,
        setAberto,
        setPainel,
        toggleDemitidos,
    } = useEscopoContext()

    // conta as quatro facetas, não só as do rail: uma faceta escondida por
    // agrupamento continua filtrando, e limpar tem que ficar disponível
    const totalSelecionado = FACETAS.reduce(
        (total, id) => total + arr(valor[id]).length,
        0,
    )
    const temFuncionarios = registro.some(({ id }) => id === 'funcionarios')

    return (
        <ShellContext.Provider value="cascata">
            <Popover open={aberto} onOpenChange={setAberto}>
                {children}
                <PopoverContent
                    align="start"
                    sideOffset={2}
                    arrow={false}
                    onFocusOutside={e => e.preventDefault()}
                    className={cn(
                        'w-[min(38rem,calc(100vw-2rem))] max-w-none',
                        'overflow-hidden rounded-md p-0 shadow-lg border bg-card',
                    )}
                >
                    <div className="flex max-sm:flex-col">
                        <nav
                            aria-label="Seções do filtro"
                            className={cn(
                                'flex shrink-0 flex-col gap-2 p-2.5 bg-popover',
                                'border-r sm:w-52 max-sm:border-r-0 max-sm:border-b max-sm:w-full',
                            )}
                        >
                            <div className="flex flex-col gap-2 max-sm:flex-row max-sm:overflow-x-auto">
                                {registro.map(({ id, multiple }) => {
                                    const total = arr(valor[id]).length
                                    const selecionada = ativa === id
                                    const Icone = ICONES_FACETA[id]

                                    return (
                                        <Button
                                            key={id}
                                            variant="outline"
                                            onClick={() => setAtiva(id)}
                                            aria-current={
                                                selecionada || undefined
                                            }
                                            title={
                                                multiple
                                                    ? `${LABEL[id]} (seleção múltipla)`
                                                    : LABEL[id]
                                            }
                                            className={cn(
                                                'relative flex items-center justify-between',
                                                'rounded-lg px-2.5 h-12',
                                                'bg-card text-base',
                                                'dark:text-primary-foreground text-foreground',
                                                'before:absolute before:left-0 before:top-2 before:bottom-2',
                                                'before:w-1 before:bg-transparent before:rounded-r-full',
                                                'before:transition-all before:duration-200',
                                                {
                                                    'before:bg-primary border-primary dark:border-primary':
                                                        selecionada,
                                                },
                                            )}
                                        >
                                            <div className="flex items-center gap-2 min-w-0 truncate">
                                                {Icone && (
                                                    <Icone
                                                        className={cn(
                                                            'size-[var(--text-lg)] shrink-0 text-foreground',
                                                        )}
                                                    />
                                                )}
                                                <span className="truncate">
                                                    {LABEL[id]}
                                                </span>
                                            </div>
                                            {total > 0 && (
                                                <Badge
                                                    variant="secondary"
                                                    className={cn(
                                                        'shrink-0 tabular-nums text-xs px-2 py-0.5 rounded-full',
                                                        'font-semibold shadow-none opacity/75',
                                                        'border text-primary-foreground bg-primary/50',
                                                    )}
                                                    title={`${total} no total`}
                                                >
                                                    {total}
                                                </Badge>
                                            )}
                                        </Button>
                                    )
                                })}
                            </div>
                            <div className="flex-1" />
                            {temFuncionarios && (
                                <div
                                    className={cn(
                                        'flex items-center justify-between',
                                        'gap-2 text-sm pb-1 px-1',
                                        'sm:border-t sm:pt-3.5',
                                    )}
                                >
                                    <Checkbox
                                        checked={!!valor.incluirDemitidos}
                                        onCheckedChange={marcado =>
                                            toggleDemitidos(marcado === true)
                                        }
                                        containerClassName="select-none"
                                        label="Incluir demitidos"
                                    />
                                </div>
                            )}
                        </nav>
                        <div className="flex min-w-0 flex-1 flex-col">
                            <div
                                ref={setPainel}
                                className={cn(ALTURA_PAINEL, 'overflow-hidden')}
                            />
                            <footer className="flex items-center justify-between gap-2 border-t bg-popover px-3.5 py-2">
                                <div className="flex items-center gap-2 truncate">
                                    <Label className="truncate text-xs text-muted-foreground select-none">
                                        Exibindo {dados.totalNoEscopo} de{' '}
                                        {dados.totalDaEmpresa} funcionários.
                                    </Label>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={limpar}
                                    disabled={!totalSelecionado}
                                    className="text-sm text-muted-foreground"
                                >
                                    <RotateCcwIcon className="size-[var(--text-sm)]" />
                                    Redefinir filtros
                                </Button>
                            </footer>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </ShellContext.Provider>
    )
}
